import type { RouterClient } from '@orpc/server'

import { createORPCClient } from '@orpc/client'
import { RPCLink } from '@orpc/client/fetch'
import { createTanstackQueryUtils } from '@orpc/tanstack-query'
import type { ChainId } from '@repo/web3'

import { getClientEnvVar } from '@/lib/config/app.config'

import type { AppRouter } from './routers'

/**
 * Client context for controlling request behavior
 */
type ClientContext = {
  /**
   * Cache mode for requests.
   * Using 'force-cache' enables Next.js native fetch caching.
   */
  cache?: RequestCache
  /**
   * Chain ID for blockchain-specific procedures.
   * Passed to server via x-chain-id header.
   */
  chainId?: ChainId
}

/**
 * Base URL for oRPC requests. Browser-only by construction.
 *
 * There is deliberately no server-side fallback: this app has no in-process oRPC caller, so a
 * server-side call here would go back out over HTTP to itself — through Vercel's Deployment
 * Protection on preview, and to a guessed port locally. Throwing turns that into an immediate,
 * legible failure instead of a 401 or a connection refused from a plausible-looking URL. Cache
 * self-invocation is a separate path with its own resolver
 * ({@link import('./cache-base-url.server').getCacheSelfInvokeBaseUrl}).
 */
function getBaseUrl() {
  if (typeof window === 'undefined') {
    throw new Error(
      'lib/orpc/client.ts is browser-only. Call the resolver or cache unit directly on the server.'
    )
  }
  return window.location.origin
}

/**
 * RPC Link configuration for oRPC client
 *
 * - Uses GET for cached requests (enables Next.js route cache)
 * - Uses POST for uncached requests
 * - Passes cache mode to fetch options
 */
const link = new RPCLink<ClientContext>({
  url: () => `${getBaseUrl()}/api/orpc`,
  method: ({ context }) => (context?.cache ? 'GET' : 'POST'),
  headers: ({ context }) => {
    const headers: Record<string, string> = {}
    if (context?.chainId) {
      headers['x-chain-id'] = String(context.chainId)
    }
    // Skew protection: pin this tab's requests to the deployment that served its JS, so a mid-session
    // deploy can't answer with a router whose payload shape the loaded bundle doesn't expect.
    const deploymentId = getClientEnvVar('VERCEL_DEPLOYMENT_ID')
    if (getClientEnvVar('VERCEL_SKEW_PROTECTION_ENABLED') === '1' && deploymentId) {
      headers['x-deployment-id'] = deploymentId
    }
    return headers
  },
  fetch: (request, init, { context }) =>
    globalThis.fetch(request, {
      ...init,
      cache: context?.cache
    })
})

/**
 * oRPC client instance
 */
const client: RouterClient<AppRouter, ClientContext> = createORPCClient(link)

/**
 * TanStack Query utilities for oRPC
 *
 * Use this in React components with useQuery, useMutation, etc.
 */
export const orpc = createTanstackQueryUtils(client)
